import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ExternalLink, Download } from "lucide-react";
import ReviewedBy from "@/components/ReviewedBy";
import Breadcrumbs from "@/components/Breadcrumbs";
import StructuredInfoBlock from "@/components/StructuredInfoBlock";
import useNoindexOnNotFound from "@/hooks/useNoindexOnNotFound";

const SITE_URL = "https://hireds.in";

function useItemSeo(item, kind, title) {
  useEffect(() => {
    if (!item) return;
    const isAdmit = kind === "admit-cards";
    const oldTitle = document.title;
    document.title = `${item.title} | ${title} | hireds.in`;
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.name = "description";
      document.head.appendChild(metaDesc);
    }
    const oldDesc = metaDesc.content;
    const dateKey = isAdmit ? item.exam_date : item.result_date;
    const dateStr = dateKey ? new Date(dateKey).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "";
    const parts = [item.title, item.organization, item.exam_name, dateStr && (isAdmit ? `Exam: ${dateStr}` : `Declared: ${dateStr}`)].filter(Boolean);
    const built = parts.join(" · ");
    metaDesc.content = (built || item.description || item.title).slice(0, 160);

    let canonical = document.querySelector('link[rel="canonical"]');
    const oldCanonical = canonical ? canonical.href : null;
    if (canonical) canonical.href = `${SITE_URL}/${kind}/${item.slug}`;
    let ogUrl = document.querySelector('meta[property="og:url"]');
    const oldOg = ogUrl ? ogUrl.content : null;
    if (ogUrl) ogUrl.content = `${SITE_URL}/${kind}/${item.slug}`;
    return () => {
      document.title = oldTitle;
      if (oldDesc !== undefined) metaDesc.content = oldDesc;
      if (oldCanonical !== null && canonical) canonical.href = oldCanonical;
      if (oldOg !== null && ogUrl) ogUrl.content = oldOg;
    };
  }, [item, kind, title]);
}

export default function ItemDetail({ kind = "admit-cards", title = "Admit Card" }) {
  const { slug } = useParams();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(()=>{ api.get(`/${kind}/${slug}`).then(r=>setItem(r.data)).catch(()=>setItem(false)).finally(()=>setLoading(false)); }, [kind, slug]);
  useItemSeo(item && typeof item === "object" ? item : null, kind, title);
  useNoindexOnNotFound(item === false);

  if (loading) return <div className="max-w-4xl mx-auto px-4 py-12 text-slate-500">Loading…</div>;
  if (!item) return <div className="max-w-4xl mx-auto px-4 py-12 text-slate-500" data-testid="not-found-message">This page is no longer available. Browse the latest <Link to={`/${kind}`} className="text-orange-600 underline">{kind === "admit-cards" ? "admit cards" : "results"}</Link>.</div>;

  const isAdmit = kind === "admit-cards";
  return (
    <article className="max-w-4xl mx-auto px-4 md:px-6 py-8" data-testid="item-detail">
      <Breadcrumbs items={[
        { label: "Home", to: "/" },
        { label: isAdmit ? "Admit Cards" : "Results", to: `/${kind}` },
        { label: item.title },
      ]} />
      <Link to={`/${kind}`} className="text-sm text-slate-600 hover:text-orange-600 inline-flex items-center gap-1 mb-4"><ArrowLeft className="w-4 h-4"/>Back</Link>
      <div className="bg-white border border-slate-200 rounded-xl p-6 md:p-8">
        <div className="text-xs uppercase tracking-wider font-bold text-orange-600">{title}</div>
        <h1 className="text-3xl md:text-4xl font-extrabold mt-1 tracking-tight" style={{fontFamily:"'Cabinet Grotesk',sans-serif"}}>{item.title}</h1>
        <div className="text-slate-600 mt-1">{item.organization} · {item.exam_name}</div>
        <ReviewedBy date={isAdmit ? item.release_date : item.result_date} />

        <div className="grid sm:grid-cols-2 gap-3 mt-6">
          {isAdmit ? (
            <>
              <Info label="Exam Date" value={fmtDate(item.exam_date)}/>
              <Info label="Released On" value={fmtDate(item.release_date)}/>
            </>
          ) : (
            <Info label="Result Declared On" value={fmtDate(item.result_date)}/>
          )}
        </div>

        {item.description && <p className="text-slate-700 mt-6 leading-relaxed">{item.description}</p>}

        <div className="flex flex-wrap gap-3 mt-6">
          {(item.download_link || item.result_link) && (
            <a href={item.download_link || item.result_link} target="_blank" rel="noopener noreferrer" data-testid="download-link">
              <Button className="bg-orange-600 hover:bg-orange-700 text-white h-11"><Download className="w-4 h-4 mr-2"/>{isAdmit?"Download Admit Card":"Check Result"}</Button>
            </a>
          )}
          {item.cutoff_link && <a href={item.cutoff_link} target="_blank" rel="noopener noreferrer"><Button variant="outline" className="h-11"><ExternalLink className="w-4 h-4 mr-2"/>View Cutoff</Button></a>}
        </div>
      </div>

      {/* Always-rendered structured summary: dates table + step-by-step download/check guide.
          Same rich template on every admit-card and result page. */}
      <StructuredInfoBlock
        kind={isAdmit ? "admit-card" : "result"}
        item={item}
      />

      {Array.isArray(item.related) && item.related.length > 0 && (
        <section className="mt-8" data-testid="related-items">
          <h2 className="text-xl md:text-2xl font-extrabold tracking-tight text-slate-900 mb-4" style={{fontFamily:"'Cabinet Grotesk',sans-serif"}}>
            Latest {isAdmit ? "Admit Cards" : "Results"}
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {item.related.map((r) => (
              <Link
                key={r.slug}
                to={`/${kind}/${r.slug}`}
                data-testid={`related-item-${r.slug}`}
                className="block bg-white border border-slate-200 hover:border-orange-400 hover:shadow-sm rounded-lg p-4 transition-colors"
              >
                <div className="text-sm font-bold text-slate-900 line-clamp-2">{r.title}</div>
                <div className="text-xs text-slate-600 mt-1">{r.organization}</div>
                <div className="text-xs text-slate-500 mt-2">
                  {isAdmit && r.exam_date ? <>Exam Date: <span className="font-semibold">{fmtDate(r.exam_date)}</span></> : null}
                  {!isAdmit && r.result_date ? <>Declared: <span className="font-semibold">{fmtDate(r.result_date)}</span></> : null}
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </article>
  );
}

function Info({ label, value }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
      <div className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-1 text-slate-900 font-semibold">{value}</div>
    </div>
  );
}
